import { ComponentType } from 'react'
import { styled } from '@mui/system'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import WbCloudyRoundedIcon from '@mui/icons-material/WbCloudyRounded'
import ThunderstormRoundedIcon from '@mui/icons-material/ThunderstormRounded'
import { colorPalette } from '../../../../../../../../style/colorPalette'

const DELAY_THRESHOLD_CAUTION = 0.1
const DELAY_THRESHOLD_DANGER = 0.3

export type ProgressVariance = {
  diff: number
  status: 'ahead' | 'behind' | 'on-track'
}

export type AlertLevel = 'ok' | 'caution' | 'danger'

export const calcProgressVariance = (
  progressRate: number,
  scheduledProgressRate: number | undefined
): ProgressVariance | undefined => {
  if (scheduledProgressRate === undefined) return undefined
  const diff = progressRate - scheduledProgressRate
  const status = diff > 0 ? 'ahead' : diff < 0 ? 'behind' : 'on-track'
  return { diff, status }
}

export const getAlertLevel = (variance: ProgressVariance): AlertLevel => {
  if (variance.status === 'ahead' || variance.status === 'on-track') return 'ok'
  const delay = -variance.diff
  if (delay <= DELAY_THRESHOLD_CAUTION) return 'ok'
  if (delay <= DELAY_THRESHOLD_DANGER) return 'caution'
  return 'danger'
}

export const formatProgressRate = (rate: number): string =>
  `${(rate * 100).toFixed(1)}%`

export const formatVarianceDiff = (diff: number): string => {
  const rounded = Math.round(diff * 1000) / 1000
  const sign = rounded > 0 ? '+' : rounded === 0 ? '±' : ''
  return `${sign}${(rounded * 100).toFixed(1)}%`
}

export const varianceColor = (status: ProgressVariance['status']): string =>
  status === 'behind' ? colorPalette.pink[5] : colorPalette.green[6]

const VarianceText = styled('span', {
  shouldForwardProp: prop => prop !== 'status' && prop !== 'isSummary',
})<{
  status: ProgressVariance['status']
  isSummary: boolean
}>(({ status, isSummary }) => ({
  fontSize: isSummary ? '13px' : '12px',
  fontWeight: 'bold',
  marginLeft: '3px',
  color: varianceColor(status),
  pointerEvents: 'none',
  flexShrink: 0,
  whiteSpace: 'nowrap',
}))

const weatherIconColors: Record<AlertLevel, string> = {
  ok: colorPalette.yellow[3],
  caution: colorPalette.monotone[3],
  danger: colorPalette.blue[8],
}

const weatherIconComponents: Record<AlertLevel, ComponentType> = {
  ok: Brightness7Icon,
  caution: WbCloudyRoundedIcon,
  danger: ThunderstormRoundedIcon,
}

const StyledWeatherIcon = styled('span', {
  shouldForwardProp: prop => prop !== 'level' && prop !== 'size',
})<{ level: AlertLevel; size: number }>(({ level, size }) => ({
  display: 'inline-flex',
  verticalAlign: 'middle',
  marginLeft: '2px',
  marginRight: '3px',
  color: weatherIconColors[level],
  '& > svg': {
    fontSize: `${size}px`,
  },
}))

export const WeatherIcon = ({
  level,
  size = 16,
}: {
  level: AlertLevel
  size?: number
}) => {
  const Icon = weatherIconComponents[level]
  return (
    <StyledWeatherIcon level={level} size={size}>
      <Icon />
    </StyledWeatherIcon>
  )
}

type VarianceIndicatorProps = {
  progressRate: number
  scheduledProgressRate: number | undefined
}

export const ProgressAlertIcon = ({
  progressRate,
  scheduledProgressRate,
  isSummary,
}: VarianceIndicatorProps & { isSummary?: boolean }) => {
  const variance = calcProgressVariance(progressRate, scheduledProgressRate)
  if (!variance) return null

  return (
    <WeatherIcon level={getAlertLevel(variance)} size={isSummary ? 18 : 16} />
  )
}

export const ProgressVarianceDiff = ({
  progressRate,
  scheduledProgressRate,
  isSummary,
}: VarianceIndicatorProps & { isSummary: boolean }) => {
  const variance = calcProgressVariance(progressRate, scheduledProgressRate)
  if (!variance) return null

  return (
    <VarianceText status={variance.status} isSummary={isSummary}>
      {formatVarianceDiff(variance.diff)}
    </VarianceText>
  )
}
